import { CoinInfo, CommonWalletInfo } from './common';

export type GetCoinInfoRes = Record<string, CoinInfo>;

export type GetCoinPriceRes = Record<string, string>;

export type GetCoinInfoParams = {
  chainCode: string;
  contractAddresses: string[];
};

/**
 * 获取token余额接口入参
 */
export type GetTokenBalanceParams = CommonWalletInfo & {
  // 币种合约地址
  tokenAddress: string;
};

/**
 * 获取token余额接口返回
 */
export type GetTokenBalanceResult = {
  // 币种链ID
  chainId: number;
  // 币种链code
  chainCode: string;
  // 币种名称
  symbol: string;
  // 币种合约地址
  contract: string;
  // 图标
  iconDayUrl: string;
  iconNightUrl: string;
  // 币种精度
  decimal: number;
  // 价格
  price: string;
  // 余额
  balance: string;
  // 余额折u
  balanceUsd: string;
};

export type GetContractBalanceParams = {
  walletAddress?: string;
  walletChainCode?: string;
  contractAddress?: string;
};

export type GetContractBalanceRes = {
  amount: string;
  chainCode: string;
  chainId: number;
  contractAddress: string;
  decimal: number;
  iconDayUrl: string;
  iconNightUrl: string;
  symbol: string;
  totalAmountInUsd: string;
  price: string;
};

export enum WalletChainTypes {
  EVM = 1,
  SUI = 2,
  SOLANA = 3,
  BTC = 4,
}

/** 钱包类型 */
export enum WalletType {
  /** 无钱包 */
  not = -1,
  /** 云钱包 */
  bybitWallet = 0,
  /** 观察者钱包 */
  watchWallet = 1,
  /** 三方钱包 */
  thirdWallet = 2,
}

export enum WalletType2 {
  NOT = 0,
  /** 云钱包，托管钱包 */
  CLOUD = 1,
  /** mpc钱包 */
  MPC = 2,
  /** AA钱包 */
  AA = 3,
  /** 私钥钱包 */
  PRIVATE_KEY = 4,
  /** 助记词钱包 */
  MNEMONIC_PHRASE = 5,
  /** 观察者钱包 */
  FOLLOW_ADDRESS = 6,
  /** metamask钱包或其他三方钱包 */
  THIRD_PARTY = 7,
}

/** 法币链信息 */
export type Token = {
  symbol: string;
  address: string;
  chainCode: string;
  iconDayUrl: string;
  iconNightUrl: string;
  isExCoin: boolean;
  chainId: number;
  type: number;
};

/** 链数据 */
export type Chain = {
  /** 链ID */
  chainId: number;
  /** 链Code */
  chainCode: string;
  /** 链名称 */
  chainName: string;
  /** 日间图标 */
  iconDayUrl: string;
  /** 夜间图标 */
  iconNightUrl: string;
  /** 基础币种 */
  baseTokenName: string;
  /** @deprecated 基础币种 */
  baseToken: string;
  /** 网络 */
  networkName: string;
  /** 排序  */
  order: number;
  /** 支持L1的Gas */
  isSupportL1Gas: number;
  /** 链类型 */
  chainType: WalletChainTypes;
  /** 区分EIP1559，暂时无用 */
  gasType: 1;
  /** txAddress */
  txAddress: string;
  /** 链是否开放 */
  tmpStatus: 'open' | 'close';
  /** 创建时间 */
  createAt: number;
  /** 最小交易金额 */
  minTransaction: string;
};

/** 链资产 */
export type ChainBalance = {
  totalAmount: string;
  chainAmountList: Array<{
    totalAmount: string;
    chainDTO: Chain;
  }>;
};

/** 钱包类型 */
export enum WalletTypes {
  /** 云钱包，托管钱包 */
  CLOUD = 'CLOUD',
  /** mpc钱包 */
  MPC = 'MPC',
  /** AA钱包 */
  AA = 'AA',
  /** 私钥钱包 */
  PRIVATE_KEY = 'PRIVATE_KEY',
  /** 助记词钱包 */
  MNEMONIC_PHRASE = 'MNEMONIC_PHRASE',
  /** 观察者钱包 */
  FOLLOW_ADDRESS = 'FOLLOW_ADDRESS',
  /** metamask钱包或其他三方钱包 */
  THIRD_PARTY = 'THIRD_PARTY',
}

export interface CurrentWallet {
  /** 标识（用于埋点上报） */
  symbol: string;
  /** 地址 */
  address: string;
  /** 别名 */
  alias: string;
  /** 链类型，（1:EVM;2:SUI） */
  chainType: WalletChainTypes;
  /** 钱包类型，（0:云钱包;1:三方钱包） */
  type: WalletType;
  /** 用户ID */
  userId?: number;
  /** 公钥 */
  custodyPubKey?: string;
  /** 链ID */
  chainIds: number[];
  /** 钱包类型 */
  walletType: WalletTypes;

  provider?: Partial<{
    stacks: any;
    solana: any;
    btc: any;
  }>;
}

export interface BtcAddressListItem {
  chainId: string;
  chainCode: string;
  address: string;
  publicKey: string;
}

export interface WalletInfo {
  pubKey?: string;
  /** 标识（用于埋点上报） */
  symbol?: string;
  /** 地址 */
  address: string;
  stxAddress?: string;
  /** 别名 */
  alias: string;
  /** 链类型，（1:EVM;2:SUI） */
  chainType: WalletChainTypes;
  /** 钱包类型，（0:云钱包;1:三方钱包） */
  type: WalletType;
  /** 用户ID */
  userId?: number;
  /** 公钥 */
  custodyPubKey?: string;
  /** 链ID */
  chainId: number;
  provider?: Partial<{
    stacks: any;
    solana: any;
    btc: any;
  }>;
  btcAddressList?: BtcAddressListItem[];
}

export type StxWalletInfo = {
  provider: any;
  address: string;
  pubKey: string;
};

export enum SupportWallets {
  bybitWallet = 0,
  thirdWallet = 1,
}

/** 钱包地址 */
export type Address = {
  /** 标识（用于埋点上报） */
  symbol: string;
  /** 地址 */
  address: string;
  /** 别名 */
  alias: string;
  /** 链类型，（1:EVM;2:SUI） */
  chainType: WalletChainTypes;
  /** 钱包类型，（0:云钱包;1:三方钱包） */
  type: WalletType;
  /** 用户ID */
  userId?: number;
  /** 公钥 */
  custodyPubKey?: string;
  /** 链ID */
  chainIds: number[];
  /** 钱包类型 */
  walletType: WalletTypes;
};

/** 三方钱包连接方式 */
export type ThirdWalletConnectType = 'ChromeExtension' | 'WalletConnect' | 'H5';

/** 三方钱包地址 */
export type ThirdAddress = Address & {
  /** 钱包logo，前端附加数据 */
  logo: string;
  /** 三方钱包连接方式：ChromeExtension 谷歌插件、walletConnect 扫码 */
  connectType: ThirdWalletConnectType;
};

/** 拓展链信息，用于拓展币种、资产等类型 */
export type WalletExtendChain = {
  /** 链ID */
  chainId: number;
  /** 链名称 */
  chainName: string;
  /** 日间图标 */
  chainIconDayUrl: string;
};
